import { useState } from "react";
import { motion } from "framer-motion";
import { Check, Loader2 } from "lucide-react";
import { ASSETS } from "../data";
import { useContent } from "../context/ContentContext";
import { GuaranteeBadge } from "./GuaranteeBadge";
import { scrollToId } from "../lib/scroll";

export const ProductBuy = ({ onBuy, loading, price }) => {
  const { content } = useContent();
  const product = content.product || {};
  const promo = content.promo || {};
  const INCLUDES = product.includes || [];
  return (
    <section id="buy" className="relative py-24 lg:py-36 max-w-[1400px] mx-auto px-6 lg:px-10">
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-px bg-white/10 border border-white/10">
        {/* Left - visual */}
        <div className="relative bg-[#0a0a0a] min-h-[420px] lg:min-h-[560px] overflow-hidden flex items-center justify-center p-10">
          <img src={ASSETS.mascot} alt="Ghost The Grid Falcon" className="h-full w-full object-contain drop-shadow-[0_0_60px_rgba(255,42,42,0.15)]" />
          <div className="absolute top-8 left-8 font-mono text-xs uppercase tracking-[0.25em] text-white/40">
            Digital Pack · v1.0
          </div>
        </div>

        {/* Right - offer */}
        <motion.div
          initial={{ opacity: 0, x: 30 }}
          whileInView={{ opacity: 1, x: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.8, ease: [0.16, 1, 0.3, 1] }}
          className="bg-[#050505] p-8 lg:p-14 flex flex-col justify-center"
        >
          <span className="font-mono text-xs uppercase tracking-[0.3em] text-accent">// The Product</span>
          <h2 className="mt-5 font-display font-black uppercase tracking-tighter text-3xl sm:text-5xl leading-[0.9]">
            {product.name || "Privacy & Disappearance Pack"}
          </h2>

          <div className="mt-8 flex items-baseline gap-4">
            <span data-testid="product-price" className="font-display font-black text-5xl sm:text-6xl">{price || "-"}</span>
            <span className="font-mono text-xs uppercase tracking-[0.2em] text-white/40">one-time · lifetime access</span>
          </div>

          {promo.enabled && promo.code && (
            <div data-testid="promo-callout" className="mt-6 inline-flex items-center gap-3 border border-accent/40 bg-accent/5 px-4 py-3">
              <span className="font-mono text-[11px] uppercase tracking-[0.15em] text-white/60">Applied automatically</span>
              <span className="font-mono text-xs font-bold tracking-[0.2em] text-accent">{promo.code}</span>
              <span className="font-mono text-[11px] uppercase tracking-[0.15em] text-white/60">{promo.text}</span>
            </div>
          )}

          <ul className="mt-10 space-y-4">
            {INCLUDES.map((item) => (
              <li key={item} className="flex items-start gap-3">
                <span className="mt-1 flex-shrink-0 h-4 w-4 flex items-center justify-center border border-accent/50">
                  <Check size={11} className="text-accent" strokeWidth={3} />
                </span>
                <span className="font-body text-sm text-white/70">{item}</span>
              </li>
            ))}
          </ul>

          <button
            data-testid="product-buy-button"
            onClick={onBuy}
            disabled={loading}
            className="group relative mt-8 inline-flex items-center justify-center gap-3 bg-accent text-white font-mono uppercase tracking-[0.15em] text-sm px-9 py-5 overflow-hidden disabled:opacity-70"
          >
            <span className="absolute inset-0 bg-white translate-y-full group-hover:translate-y-0 transition-transform duration-300 ease-[cubic-bezier(0.16,1,0.3,1)]" />
            <span className="relative z-10 flex items-center gap-3 group-hover:text-black transition-colors duration-300">
              {loading ? <><Loader2 size={18} className="animate-spin" /> Opening secure checkout…</> : <>Buy & Disappear Now</>}
            </span>
          </button>
          <p className="mt-4 font-mono text-[11px] uppercase tracking-[0.2em] text-white/30">
            Secure checkout by Stripe · Card & Apple Pay · Instant delivery · GHOST20 applied automatically, no code to type
          </p>
          <button
            data-testid="product-free-tips-link"
            onClick={() => scrollToId("#free-tips")}
            className="mt-4 font-mono text-[11px] uppercase tracking-[0.2em] text-white/45 hover:text-accent transition-colors underline underline-offset-4 decoration-white/20"
          >
            Not ready? Get 5 free tips first →
          </button>
          <div className="mt-5"><GuaranteeBadge /></div>
        </motion.div>
      </div>
    </section>
  );
};
